import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './ManuallyAddPaper.css';

const AddPaper = () => {
  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    keywords: '',
    mainDomain: '',
    subDomain1: '',
    subDomain2: '',
    authors: [{ firstName: '', lastName: '', email: '' }],
    file: null,
  });
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-cutomize-domains`);
        setDomains(response.data);
      } catch (error) {
        console.error('Error fetching domains:', error);
      }
    };
    fetchDomains();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    if (name === "keywords") {
      const keywordsArray = value.split(',').map((keyword) => keyword.trim());
      if (keywordsArray.length > 5) {
        Swal.fire({
          icon: 'warning',
          title: 'Maximum 5 keywords allowed',
          text: 'Please enter no more than 5 keywords.',
        });
        return;
      }
    }
  
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  

  const handleAuthorChange = (index, e) => {
    const { name, value } = e.target;
    const authors = [...formData.authors];
    authors[index][name] = value;
    setFormData((prev) => ({
      ...prev,
      authors,
    }));
  };

  const handleAddAuthor = () => {
    setFormData((prev) => ({
      ...prev,
      authors: [...prev.authors, { firstName: '', lastName: '', email: '' }],
    }));
  };

  const handleRemoveAuthor = (index) => {
    const authors = formData.authors.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      authors,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('abstract', formData.abstract);
    formDataToSend.append('keywords', formData.keywords);
    
    // Only include non-empty domains in the array
    const paperDomainsArray = [formData.mainDomain];
    if (formData.subDomain1) {
      paperDomainsArray.push(formData.subDomain1);
    }
    if (formData.subDomain2) {
      paperDomainsArray.push(formData.subDomain2);
    }
    
    formDataToSend.append('paperDomains', JSON.stringify(paperDomainsArray));
    formDataToSend.append('authors', JSON.stringify(formData.authors));
    formDataToSend.append('file', formData.file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/manually-add-paper`,
        formDataToSend,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setLoading(false);
      Swal.fire({
        icon: 'success',
        title: 'Paper added successfully!',
      });
      navigate('/admin/customize-paper');
    } catch (error) {
      setLoading(false);
      console.error('Error submitting paper:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed!',
        text: 'Failed to add paper.',
      });
    }
  };

  const handleMainDomainChange = (e) => {
    const mainDomain = e.target.value;
    setFormData((prev) => ({
      ...prev,
      mainDomain,
      subDomain: '',
    }));
  };

  const handleSubDomainChange1 = (e) => {
    setFormData((prev) => ({
      ...prev,
      subDomain1: e.target.value,
    }));
  };

  const handleSubDomainChange2 = (e) => {
    setFormData((prev) => ({
      ...prev,
      subDomain2: e.target.value,
    }));
  };

  return (
    <div className="add-paper-wrapper">
      <div className="add-paper-container">
        <div className="header-section">
          <h1 className="page-title">Add Paper Manually</h1>
          <p className="page-subtitle">Submit a new paper to the conference system</p>
        </div>

        <form onSubmit={handleSubmit} className="paper-form">
          {/* Title */}
          <div className="form-group">
            <label className="form-label">Paper Title <span className="required">*</span></label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter paper title"
            />
          </div>

          {/* Abstract */}
          <div className="form-group">
            <label className="form-label">Abstract <span className="required">*</span></label>
            <textarea
              name="abstract"
              value={formData.abstract}
              onChange={handleChange}
              required
              className="form-textarea"
              placeholder="Enter paper abstract"
            />
          </div>

          {/* Keywords */}
          <div className="form-group">
            <label className="form-label">Keywords <span className="helper-text">(Maximum 5, separated by comma)</span></label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Machine Learning, AI, Deep Learning"
            />
          </div>

          {/* Domain Selection Card */}
          <div className="domain-selection-card">
            <h3 className="card-title">Domain Selection</h3>
            
            {/* Main Domain */}
            <div className="form-group">
              <label className="form-label">Main Domain <span className="required">*</span></label>
              <select
                name="mainDomain"
                value={formData.mainDomain}
                onChange={handleMainDomainChange}
                required
                className="form-select"
              >
                <option value="">Select Main Domain</option>
                {domains.map((domain) => (
                  <option key={domain.mainDomain} value={domain.mainDomain}>
                    {domain.mainDomain}
                  </option>
                ))}
              </select>
            </div>

            {/* Sub Domain 1 */}
            <div className="form-group">
              <label className="form-label">Sub Domain 1 <span className="optional">(Optional)</span></label>
              <select
                name="subDomain1"
                value={formData.subDomain1}
                onChange={handleSubDomainChange1}
                disabled={!formData.mainDomain}
                className="form-select"
              >
                <option value="">Select Sub Domain 1</option>
                {domains
                  .find((domain) => domain.mainDomain === formData.mainDomain)
                  ?.subDomains.map((subDomain, index) => (
                    <option key={index} value={subDomain}>
                      {subDomain}
                    </option>
                  ))}
              </select>
            </div>

            {/* Sub Domain 2 */}
            <div className="form-group">
              <label className="form-label">Sub Domain 2 <span className="optional">(Optional)</span></label>
              <select
                name="subDomain2"
                value={formData.subDomain2}
                onChange={handleSubDomainChange2}
                disabled={!formData.mainDomain}
                className="form-select"
              >
                <option value="">Select Sub Domain 2</option>
                {domains
                  .find((domain) => domain.mainDomain === formData.mainDomain)
                  ?.subDomains.map((subDomain, index) => (
                    <option key={index} value={subDomain}>
                      {subDomain}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Authors Section */}
          <div className="authors-card">
            <div className="card-header">
              <h3 className="card-title">
                <svg className="title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Authors
              </h3>
              <span className="author-count">{formData.authors.length} Author{formData.authors.length > 1 ? 's' : ''}</span>
            </div>

            {formData.authors.map((author, index) => (
              <div key={index} className="author-entry">
                <div className="author-entry-header">
                  <span className="author-number">Author {index + 1}</span>
                  {formData.authors.length > 1 && (
                    <button
                      type="button"
                      className="remove-author-btn"
                      onClick={() => handleRemoveAuthor(index)}
                    >
                      <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  )}
                </div>
                <div className="author-fields">
                  <div className="form-group">
                    <label className="form-label">First Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="firstName"
                      value={author.firstName}
                      onChange={(e) => handleAuthorChange(index, e)}
                      required
                      className="form-input"
                      placeholder="First name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="lastName"
                      value={author.lastName}
                      onChange={(e) => handleAuthorChange(index, e)}
                      required
                      className="form-input"
                      placeholder="Last name"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label className="form-label">Email <span className="required">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={author.email}
                      onChange={(e) => handleAuthorChange(index, e)}
                      required
                      className="form-input"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              className="add-author-btn"
              onClick={handleAddAuthor}
            >
              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Add Another Author
            </button>
          </div>

          {/* File Upload */}
          <div className="file-upload-card">
            <label className="form-label">Upload Paper <span className="required">*</span></label>
            <div className="file-upload-area">
              <input 
                type="file" 
                onChange={handleFileChange} 
                required 
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="file-upload-label">
                <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="upload-text">
                  {formData.file ? formData.file.name : 'Click to upload or drag and drop'}
                </span>
                <span className="upload-hint">PDF, DOC, DOCX (MAX. 10MB)</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <div className="loading-spinner-small"></div>
                Submitting Paper...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Submit Paper
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddPaper;